import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
    type Dispatch,
    type SetStateAction,
} from 'react';
import axios from 'axios';
import settings from '@/settings';
import {sanitizeCSS} from '@/shared/utils/cssSanitizer';
import {resolveMediaUrl} from '@/shared/utils/mediaUrl';
import {logError, logWarn} from '@/shared/utils/logger';
import {parseCustomCss, hexToRgbTriplet} from '@/modules/admin/utils/customCssTokens';

/**
 * Auto-derive elevated-surface tokens from the tenant's page-background
 * override.  Lets a tenant pick *just* a page background (light or dark) and
 * have nav strips, table headers, popovers, and surface text fall into a
 * sensible matching shade without forcing them to design every related
 * token.  Designers retain explicit override capability via the picker —
 * any token the tenant set explicitly is left untouched here.
 *
 * Returns a string of additional `:root { ... }` declarations to inject
 * alongside the tenant's sanitized custom_css, or null if no derivation is
 * needed (no page-bg override, or every surface token already overridden).
 */
function deriveSurfaceTokensFromCustomCss(customCss: string): string | null {
    const parsed = parseCustomCss(customCss);
    const pageBgHex = parsed.tokenValues['mld-background'];
    if (!pageBgHex) return null; // tenant didn't override page bg → defaults apply

    const pageRgb = hexToRgbArray(pageBgHex);
    if (!pageRgb) return null;

    const isDarkPage = relativeLuminance(pageRgb) < 0.5;

    // Each derived token mirrors the role of its picker counterpart:
    //   menu-background  : elevated surface — slightly *off* the page bg
    //   panel-bg         : card surface — matches page bg
    //   menu-text        : text on menu-background, chosen for contrast
    //   text-primary     : body text, chosen for contrast against page bg
    //   text-secondary / muted-text — derived as softer tones of text-primary
    //   panel-border     : subtle delineation, picks low-contrast tone
    const derived: Record<string, [number, number, number]> = {};

    if (!parsed.tokenValues['mld-menu-background']) {
        derived['mld-menu-background'] = isDarkPage ? lightenRgb(pageRgb, 0.12) : darkenRgb(pageRgb, 0.025);
    }
    if (!parsed.tokenValues['mld-panel-bg']) {
        derived['mld-panel-bg'] = pageRgb;
    }
    if (!parsed.tokenValues['mld-menu-text']) {
        derived['mld-menu-text'] = isDarkPage ? [240, 241, 235] : [1, 59, 77];
    }
    if (!parsed.tokenValues['mld-text-primary']) {
        derived['mld-text-primary'] = isDarkPage ? [240, 241, 235] : [0, 60, 60];
    }
    if (!parsed.tokenValues['mld-text-secondary']) {
        derived['mld-text-secondary'] = isDarkPage ? [200, 205, 200] : [90, 125, 124];
    }
    if (!parsed.tokenValues['mld-muted-text']) {
        derived['mld-muted-text'] = isDarkPage ? [150, 155, 145] : [119, 121, 111];
    }
    if (!parsed.tokenValues['mld-panel-border']) {
        derived['mld-panel-border'] = isDarkPage ? lightenRgb(pageRgb, 0.2) : [187, 187, 175];
    }

    if (Object.keys(derived).length === 0) return null;
    const lines = Object.entries(derived)
        .map(([name, rgb]) => `    --${name}: ${rgb.join(' ')} !important;`)
        .join('\n');
    return `:root {\n${lines}\n}`;
}

function hexToRgbArray(hex: string): [number, number, number] | null {
    const triplet = hexToRgbTriplet(hex);
    if (!triplet) return null;
    const parts = triplet.split(/\s+/).map(Number);
    if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return null;
    return [parts[0], parts[1], parts[2]];
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
    const norm = (c: number) => {
        const x = c / 255;
        return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * norm(r) + 0.7152 * norm(g) + 0.0722 * norm(b);
}

function lightenRgb([r, g, b]: [number, number, number], factor: number): [number, number, number] {
    return [
        Math.min(255, Math.round(r + (255 - r) * factor)),
        Math.min(255, Math.round(g + (255 - g) * factor)),
        Math.min(255, Math.round(b + (255 - b) * factor)),
    ];
}

function darkenRgb([r, g, b]: [number, number, number], factor: number): [number, number, number] {
    return [
        Math.max(0, Math.round(r * (1 - factor))),
        Math.max(0, Math.round(g * (1 - factor))),
        Math.max(0, Math.round(b * (1 - factor))),
    ];
}

export interface Organization {
    id: number;
    subdomain: string;
    name: string;
    display_name: string;
    support_email?: string;
    support_phone?: string;
    features?: OrganizationFeatures;
}

export interface OrganizationFeatures {
    enable_appointments?: boolean;
    enable_kit_registration?: boolean;
    enable_telehealth?: boolean;
    enable_patient_switcher?: boolean;
    enable_health_profile?: boolean;
    enable_employee_health_dashboard?: boolean;
    /** OHD-side organization id (string or number depending on import path) */
    ohd_organization_id?: number | string | null;
}

export interface OrganizationTheme {
    logo_url: string;
    favicon_url: string;
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    font_family: string;
    custom_css?: string;
    welcome_message?: string;
    footer_text?: string;
}

interface TenantContextType {
    organization: Organization | null;
    theme: OrganizationTheme | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
    organization: null,
    theme: null,
    loading: true,
    error: null,
    refetch: async () => {},
});

function normalizeThemeUrls(apiURL: string, theme: OrganizationTheme): OrganizationTheme {
    return {
        ...theme,
        logo_url: resolveMediaUrl(theme.logo_url, apiURL),
        favicon_url: resolveMediaUrl(theme.favicon_url, apiURL),
    };
}

/**
 * Get the static subdomain override from VITE_TENANT_SUBDOMAIN.
 * Only needed when running on localhost without a reverse proxy.
 * In all proxied environments (dev, staging, production) the browser Origin
 * header already carries the correct tenant subdomain.
 */
function getSubdomainOverride(): string | null {
    const envSubdomain = import.meta.env.VITE_TENANT_SUBDOMAIN;
    return envSubdomain ? String(envSubdomain).trim() : null;
}

/**
 * Bounded wait for the tenant boot requests. AppContent gates the ENTIRE app
 * behind the "Loading organization..." spinner while these are in flight —
 * without a timeout, an API that accepts the connection but never answers
 * leaves the user on that spinner forever with no error state.
 */
const TENANT_FETCH_TIMEOUT_MS = 15000;

type LoadTenantDataParams = {
    apiURL: string;
    applyTheme: (theme: OrganizationTheme, org: Organization) => void;
    setOrganization: Dispatch<SetStateAction<Organization | null>>;
    setTheme: Dispatch<SetStateAction<OrganizationTheme | null>>;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setError: Dispatch<SetStateAction<string | null>>;
};

async function loadTenantData({
    apiURL,
    applyTheme,
    setOrganization,
    setTheme,
    setLoading,
    setError,
}: LoadTenantDataParams): Promise<void> {
    const subdomainOverride = getSubdomainOverride();

    try {
        setLoading(true);
        setError(null);

        const headers: Record<string, string> = {};
        if (subdomainOverride) {
            headers['X-Tenant-Subdomain'] = subdomainOverride;
        }

        const orgResponse = await axios.get<Organization>(`${apiURL}/api/organization`, {
            headers,
            timeout: TENANT_FETCH_TIMEOUT_MS,
        });
        const org = orgResponse.data;
        setOrganization(org);

        const themeResponse = await axios.get<OrganizationTheme>(`${apiURL}/api/organization/theme`, {
            headers,
            timeout: TENANT_FETCH_TIMEOUT_MS,
        });
        const themeData = normalizeThemeUrls(apiURL, themeResponse.data);
        setTheme(themeData);

        applyTheme(themeData, org);
    } catch (err: any) {
        logError('Failed to load tenant data', err);
        setError(err.response?.data?.error || 'Failed to load organization');
    } finally {
        setLoading(false);
    }
}

export function TenantProvider({children}: {children: ReactNode}) {
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [theme, setTheme] = useState<OrganizationTheme | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const applyTheme = useCallback((theme: OrganizationTheme, org: Organization) => {
        // Set CSS variables
        const root = document.documentElement;
        // Legacy variables — still consumed by .btn-primary / .bg-primary
        // utility classes in theme.css and by any existing tenant custom_css
        // that targets them.  These are NOT the MLD brand tokens; do not
        // propagate them onto --mld-* even when they're set, because many
        // existing org records carry the legacy `#0066CC` default which
        // would rebrand the entire MLD surface blue.
        root.style.setProperty('--primary-color', theme.primary_color);
        root.style.setProperty('--secondary-color', theme.secondary_color);
        root.style.setProperty('--accent-color', theme.accent_color);
        // Only honor a *deliberate* per-tenant font override. Many org records
        // carry the legacy seeded `Inter, …` default; applying it would clobber
        // the Poppins brand default (theme.css) app-wide, so we skip it and let
        // Poppins win — same rationale as the legacy `#0066CC` color default
        // above. A real custom font still flows through.
        if (theme.font_family && !/^\s*inter\b/i.test(theme.font_family)) {
            root.style.setProperty('--font-family', theme.font_family);
        }

        // White-labeling of the MLD brand surface happens via
        // organization_themes.custom_css, NOT primary/secondary/accent.
        // A designer overrides --mld-primary etc. directly with the
        // R G B triplet syntax documented in STYLE_GUIDE §2.  The custom
        // CSS injection happens below (search for custom_css).

        // Set page title
        document.title = org.display_name || org.name;

        // Update favicon
        const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (favicon && theme.favicon_url) {
            favicon.href = theme.favicon_url;
        } else if (theme.favicon_url) {
            // Create favicon link if it doesn't exist
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.href = theme.favicon_url;
            document.head.appendChild(newFavicon);
        }

        // Apply custom CSS if provided (with sanitization to prevent XSS)
        if (theme.custom_css) {
            // Remove existing custom org styles if they exist
            const existingStyle = document.getElementById('custom-org-styles');
            if (existingStyle) {
                existingStyle.remove();
            }

            // Sanitize CSS to prevent XSS attacks and data exfiltration
            const sanitizedCSS = sanitizeCSS(theme.custom_css);

            if (sanitizedCSS) {
                // When the tenant overrode page bg but left related surface
                // / text tokens at default, derive matching values from the
                // page bg's lightness so dark themes don't look broken out
                // of the box.  Designers retain control — anything they
                // explicitly set in custom_css wins (the derivation skips
                // those tokens).
                const derivedSurfaces = deriveSurfaceTokensFromCustomCss(theme.custom_css);
                const finalCSS = derivedSurfaces ? `${sanitizedCSS}\n\n${derivedSurfaces}` : sanitizedCSS;

                // Add new custom styles (only if sanitization didn't reject everything)
                const style = document.createElement('style');
                style.id = 'custom-org-styles';
                style.textContent = finalCSS;
                document.head.appendChild(style);
            } else {
                logWarn('Custom CSS was rejected by sanitizer');
            }
        }
    }, []);

    const refetch = useCallback(
        () =>
            loadTenantData({
                apiURL: settings.apiURL,
                applyTheme,
                setOrganization,
                setTheme,
                setLoading,
                setError,
            }),
        [applyTheme]
    );

    useEffect(() => {
        void loadTenantData({
            apiURL: settings.apiURL,
            applyTheme,
            setOrganization,
            setTheme,
            setLoading,
            setError,
        });
    }, [applyTheme]);

    return (
        <TenantContext.Provider value={{organization, theme, loading, error, refetch}}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
}
